import { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useForm } from 'react-hook-form';
import { getWorkflowFormAction } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import * as yup from 'yup';
import { formResolver } from 'src/utils/formResolver';
import { booleanScheme, stringEmailScheme, stringScheme } from 'src/utils/validation-schemes';
import { WorkflowsEditIcon } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/WorkflowsRowIcons';
import { WorkflowNameAndDescriptionBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowNameAndDescriptionBlock';
import { WorkflowActionsBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowActionsBlock';
import { WorkflowTriggersBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowTriggersBlock';
import { getWorkflowRequestAction } from 'src/pages/ConfigurationPage/WorkflowsTab/getWorkflowRequestAction';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';
import { useNotification } from 'src/hooks/useNotification';
import type {
  Workflow,
  WorkflowFormValues,
} from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';

// A workflow stores one action or a list of them; the form always edits a list.
const toActionList = <T,>(action: T | T[] | undefined | null): T[] => {
  if (Array.isArray(action)) return action;
  return action ? [action] : [];
};

export const EditWorkflowPopup = ({
  row = {} as Workflow,
  onSubmit,
  isCustom = false,
  disabled = false,
}: {
  row?: Workflow;
  onSubmit: (workflow: Workflow) => void;
  isCustom?: boolean;
  disabled?: boolean;
}) => {
  const [workflow, setWorkflow] = useState(row);
  const [open, setOpen] = useState(false);
  const notification = useNotification();

  useEffect(() => {
    if (row) {
      setWorkflow(row);
    }
  }, [row]);

  const { handleSubmit, control, reset, watch, trigger, setError } = useForm<WorkflowFormValues>({
    defaultValues: async () => {
      const action = toActionList(workflow.action);

      return {
        name: workflow.name || '',
        description: workflow.description || '',
        trigger_type: workflow.trigger_type || 'when_asked',
        trigger_value: workflow.trigger_value || '',
        action: action.map(getWorkflowFormAction),
      };
    },
    resolver: formResolver(
      yup.object({
        name: stringScheme({ required: true }),
        description: stringScheme({ required: false }),
        trigger_type: stringScheme({ required: false }),
        trigger_value: stringScheme({ required: isCustom }),
        action: yup.array(
          yup.object({
            action_type: stringScheme({ required: false }),
            action_message: stringScheme({ required: false }),
            action_escalated_list: stringScheme({ required: false }),
            action_data_collection: stringScheme({ required: false }),
            action_fields_name: booleanScheme({ required: false }),
            action_fields_email: booleanScheme({ required: false }),
            action_fields_phone_number: booleanScheme({ required: false }),
            action_fields_custom: booleanScheme({ required: false }),
            action_message_type: stringScheme({ required: false }),
            message_before: stringScheme({ required: false }),
            receiver_email_addresses: stringEmailScheme({ required: false, format: true }),
            message_after: stringScheme({ required: false }),
            message_close: stringScheme({ required: false }),
            action_fields_custom_array: yup.array(
              yup.object({
                name: stringScheme({ required: false }),
                description: stringScheme({ required: false }),
              }),
            ),
          }),
        ),
      }),
    ),
  });

  const submit = (data: WorkflowFormValues) => {
    const isValid = validateWorkflowActions(data, setError);

    if (!isValid) {
      return;
    }

    const action = data.action.map((action) => getWorkflowRequestAction(action));

    const newWorkflow: Workflow = {
      enabled: workflow.enabled,
      type: workflow.type,
      action: isCustom ? action : action[0],
    };

    if (isCustom) {
      newWorkflow.name = data.name;
      newWorkflow.description = data.description;
      newWorkflow.trigger_type = data.trigger_type;
      newWorkflow.trigger_value = data.trigger_value;
    }

    onSubmit(newWorkflow);
    notification.success(
      `${isCustom ? 'Custom workflow ' : 'Escalation '} settings have been updated successfully!`,
    );
    close();
  };

  const close = () => {
    setOpen(false);
  };

  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
    }

    setOpen(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        disabled={disabled}
        asChild
        onClick={(e) => e.stopPropagation()}
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <WorkflowsEditIcon />
      </DialogTrigger>
      <DialogContent closeButtonHidden className={'overflow-hidden'}>
        <DialogHeader>
          <DialogTitle>{isCustom ? 'Custom workflow' : 'Escalation'} settings</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="content">
            <form
              id="edit-custom-workflow-form"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(submit)(e);
              }}
              className={'max-h-[60vh] overflow-auto pr-4'}
            >
              <div className={'flex flex-col h-full w-full gap-5'}>
                {isCustom && (
                  <WorkflowNameAndDescriptionBlock control={control} isCustom={isCustom} />
                )}
                {isCustom && <WorkflowTriggersBlock control={control} isCustom={isCustom} />}
                <WorkflowActionsBlock
                  control={control}
                  watch={watch}
                  isCustom={isCustom}
                  trigger={trigger}
                />
              </div>
            </form>
            <div className="flex justify-center mt-5">
              <div className="flex gap-2.5">
                <Button
                  variant="outline"
                  type="button"
                  className="min-w-24"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button form="edit-custom-workflow-form" className="min-w-24">
                  Update
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
